import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Tableau de bord à widgets d'un plan d'action (et de l'index des plans).
// Persistance selon persistMode :
//   - "plan"       -> PATCH update_dashboard { dashboard_widgets: JSON } (partagé)
//   - "saved_view" -> PATCH saved_view { config }  (index sur vue enregistrée)
//   - autre        -> param d'URL dashboard_widgets (transitoire)
export default class extends Controller {
  static targets = [
    "grid", "widget", "picker", "typeOption",
    "metricSelect", "groupBySelect", "groupBySection",
    "timeAxisSelect", "timeAxisSection", "titleInput", "sizeSelect", "editToggle"
  ]
  static values = {
    widgets:     Array,
    catalog:     Object,
    editable:    Boolean,
    persistMode: String,
    persistUrl:  String
  }

  connect() {
    this._currentType = "kpi"
    this._refreshTypeUI()
    if (this.editableValue && this.hasGridTarget) {
      this.sortable = Sortable.create(this.gridTarget, {
        animation: 150, handle: ".widget__action--drag", draggable: ".widget",
        ghostClass: "widget--ghost", dragClass: "widget--dragging",
        onEnd: this._onReorder.bind(this)
      })
    }
    this._keydown = (e) => { if (e.key === "Escape" && this.hasPickerTarget && !this.pickerTarget.hidden) this.closePicker() }
    document.addEventListener("keydown", this._keydown)
  }

  disconnect() {
    this.sortable?.destroy()
    document.removeEventListener("keydown", this._keydown)
  }

  toggleEdit() {
    const dash = this.element.querySelector(".dashboard")
    if (!dash) return
    const editing = dash.classList.toggle("is-editing")
    this.element.classList.toggle("is-dashboard-editing", editing)
    if (this.hasEditToggleTarget) this.editToggleTarget.classList.toggle("is-active", editing)
  }

  openPicker() {
    if (!this.editableValue) return
    this.pickerTarget.hidden = false
    if (this.hasTitleInputTarget) {
      this.titleInputTarget.value = ""
      setTimeout(() => this.titleInputTarget.focus(), 60)
    }
  }

  closePicker() {
    if (this.hasPickerTarget) this.pickerTarget.hidden = true
  }

  selectType(event) {
    this._currentType = event.currentTarget.dataset.typeValue
    this._refreshTypeUI()
  }

  _refreshTypeUI() {
    this.typeOptionTargets.forEach(b => b.classList.toggle("is-active", b.dataset.typeValue === this._currentType))
    const t = this._currentType
    if (this.hasGroupBySectionTarget) this.groupBySectionTarget.hidden = !["chart_donut", "chart_bar", "table_top"].includes(t)
    if (this.hasTimeAxisSectionTarget) this.timeAxisSectionTarget.hidden = t !== "chart_line"
  }

  addWidget() {
    const t = this._currentType
    const w = {
      id:     this._newId(),
      type:   t,
      title:  (this.titleInputTarget.value || "").trim() || this._defaultTitle(t),
      metric: this.metricSelectTarget.value,
      size:   parseInt(this.sizeSelectTarget.value, 10) || 1
    }
    if (["chart_donut", "chart_bar", "table_top"].includes(t)) w.group_by = this.groupBySelectTarget.value
    if (t === "chart_line") w.time_axis = this.timeAxisSelectTarget.value
    if (t === "table_top") w.limit = 5
    this._persist([...this.widgetsValue, w])
  }

  removeWidget(event) {
    const id = event.currentTarget.dataset.widgetId
    if (!id || !confirm("Supprimer ce widget ?")) return
    this._persist(this.widgetsValue.filter(w => w.id !== id))
  }

  changeSize(event) {
    const id   = event.currentTarget.dataset.widgetId
    const size = parseInt(event.currentTarget.value, 10) || 1
    this._persist(this.widgetsValue.map(w => w.id === id ? { ...w, size } : w))
  }

  _onReorder() {
    const ids    = Array.from(this.gridTarget.querySelectorAll(".widget")).map(el => el.dataset.widgetId)
    const byId   = new Map(this.widgetsValue.map(w => [w.id, w]))
    const widgets = ids.map(id => byId.get(id)).filter(Boolean)
    if (widgets.length !== this.widgetsValue.length) return
    this._persist(widgets)
  }

  async _persist(widgets) {
    const mode = this.persistModeValue
    if (mode === "plan" && this.persistUrlValue) {
      const res = await fetch(this.persistUrlValue, {
        method: "PATCH", headers: this._headers(),
        body: JSON.stringify({ dashboard_widgets: JSON.stringify(widgets) })
      })
      res.ok ? window.location.reload() : alert("Erreur lors de l'enregistrement du tableau de bord.")
    } else if (mode === "saved_view" && this.persistUrlValue) {
      const config = { view_type: "dashboard", widgets, filters: {} }
      const res = await fetch(this.persistUrlValue, {
        method: "PATCH", headers: this._headers(),
        body: JSON.stringify({ saved_view: { config: JSON.stringify(config) } })
      })
      res.ok ? window.location.reload() : alert("Erreur lors de la mise à jour de la vue.")
    } else {
      const url = new URL(window.location.href)
      url.searchParams.set("view_type", "dashboard")
      url.searchParams.set("dashboard_widgets", JSON.stringify(widgets))
      window.location.href = url.toString()
    }
  }

  _newId() {
    return "w-" + Date.now().toString(36) + "-" + Math.floor(Math.random() * 1e6).toString(36)
  }

  _defaultTitle(type) {
    const cat       = this.catalogValue || {}
    const metric    = (cat.metrics || []).find(m => m.value === this.metricSelectTarget.value)
    const typeLabel = (cat.widget_types || []).find(t => t.value === type)?.label
    if (type === "kpi") return metric?.label || "Indicateur"
    if (type === "chart_line") {
      const axis = (cat.time_axes || []).find(a => a.value === this.timeAxisSelectTarget.value)?.label
      return [metric?.label, axis].filter(Boolean).join(" · ") || (typeLabel || "Graphique")
    }
    const grp = (cat.group_by || []).find(g => g.value === this.groupBySelectTarget.value)?.label
    return [metric?.label, grp].filter(Boolean).join(" par ") || (typeLabel || "Widget")
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf || "" }
  }
}
